import { useTranslation } from "react-i18next";
import { useContext, useState } from "react";
import { ClientContext, ClientContextType } from "../storage/ClientStorage";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from "./DropdownMenu";
import { removeToken, getToken } from "@/services/utils/store";
import { Avatar, AvatarFallback, AvatarImage } from "./Avatar";
import { Link, useNavigate } from "react-router-dom";
import { MapPin, ClipboardList, Lock, Download, LogOut } from "lucide-react";
import ChangePassword from "./ChangePassword";

export function Account() {
  const { t } = useTranslation();
  const { state, dispatch } = useContext<ClientContextType>(ClientContext);
  const navigate = useNavigate();
  const user = state.user;
  const [showChangePassword, setShowChangePassword] = useState(false);

  const handleLogout = () => {
    removeToken();
    dispatch({ ...state, token: null });
    window.location.reload();
  };

  const handleDownloadDatabase = async () => {
    try {
      const token = getToken();
      const apiUrl = import.meta.env.VITE_API_URL;
      const response = await fetch(`${apiUrl}/api/export/database`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error("Error al descargar");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const date = new Date().toISOString().split("T")[0];
      a.download = `database_export_${date}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Error downloading database:", error);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Avatar className="cursor-pointer size-11 md:size-8">
            {user?.avatarUrl ? (
              <AvatarImage src={user.avatarUrl} alt={user.name || "@user"} />
            ) : null}
            <AvatarFallback>
              {user?.name ? user.name.charAt(0).toUpperCase() : "M"}
            </AvatarFallback>
          </Avatar>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-56">
          <DropdownMenuLabel className="text-xs text-gray-500">
            {user?.name || "Usuario"}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuGroup>
            <DropdownMenuItem
              className="cursor-pointer flex items-center gap-2"
              onClick={() => navigate("/dashboard/polygoneditor")}
            >
              <MapPin size={16} />
              Administrar polígonos
            </DropdownMenuItem>
            <DropdownMenuItem
              className="cursor-pointer flex items-center gap-2"
              onClick={() => navigate("/dashboard/trips")}
            >
              <ClipboardList size={16} />
              Administrar viajes
            </DropdownMenuItem>
          </DropdownMenuGroup>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="cursor-pointer flex items-center gap-2"
            onClick={() => setShowChangePassword(true)}
          >
            <Lock size={16} />
            Cambiar contraseña
          </DropdownMenuItem>
          <DropdownMenuItem
            className="cursor-pointer flex items-center gap-2"
            onClick={handleDownloadDatabase}
          >
            <Download size={16} />
            Descarga base de datos
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="cursor-pointer flex items-center gap-2 text-red-600"
            onClick={handleLogout}
          >
            <LogOut size={16} />
            Cerrar sesión
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <ChangePassword
        isOpen={showChangePassword}
        onClose={() => setShowChangePassword(false)}
        onSuccess={() => setShowChangePassword(false)}
      />
    </>
  );
}
